import { getChartLabel } from "./chartLabel";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Form } from "react-bootstrap";
import CustomLegend from "./CustomLegend";
import CustomTooltip from "./CustomTooltip";

const EIDCharts = ({ data, filters, selectedState }) => {
  const [chartDataByService, setChartDataByService] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [sortConfigs, setSortConfigs] = useState({});

  const descriptions = [
    "Number of children tested as per EID Cascade 6 Weeks",
    "Number of children tested as per EID Cascade 6 Months",
    "Number of children tested as per EID Cascade 12 Months",
  ];

  const services = [
    "Target",
    "No_tested",
    "Left_Outs",
    "Found_HIV_Positive",
    "Positivity_Rate",
  ];

  const services1 = ["Target", "No_tested", "Left_Outs", "Found_HIV_Positive"];

  const formatServiceName = (service) =>
    service.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  // const serviceOrder = services1.map((s) => formatServiceName(s));
  const serviceOrder = services1; // raw keys only
  const descriptionTitles = descriptions.reduce((acc, desc) => {
    acc[desc] = desc;
    return acc;
  }, {});

  const otherEntries = Array.isArray(data)
    ? data.filter((row) => row.Indicator === "Other (Specify)")
    : [];

  // Organize chart data by service and place
  useEffect(() => {
    const organizedData = {};
    services.forEach((service) => {
      const chartData = [];
      if (!Array.isArray(data)) return;
      const places = [...new Set(data.map((entry) => entry.Place))];
      places.forEach((place) => {
        const entry = { Place: place };
        descriptions.forEach((desc) => {
          const item = data.find(
            (row) =>
              row.Place?.trim().toLowerCase() === place.trim().toLowerCase() &&
              row.Indicator?.trim().toLowerCase() === desc.trim().toLowerCase(),
          );
          entry[desc] =
            item && !isNaN(item[service]) ? parseFloat(item[service]) : 0;
        });
        chartData.push(entry);
      });
      organizedData[service] = chartData;
    });
    setChartDataByService(organizedData);
  }, [data]);

  if (!Array.isArray(data) || data.length === 0)
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <h5 style={{ color: "#666", fontSize: "20px" }}>No data available</h5>
      </div>
    );

  const places = Array.from(
    new Set(
      Object.values(chartDataByService)
        .flat()
        .map((entry) => entry.Place),
    ),
  );

  const handleSort = (tableKey, columnKey) => {
    setSortConfigs((prev) => {
      const prevConfig = prev[tableKey] || { key: null, direction: "asc" };
      if (prevConfig.key === columnKey) {
        return {
          ...prev,
          [tableKey]: {
            key: columnKey,
            direction: prevConfig.direction === "asc" ? "desc" : "asc",
          },
        };
      }
      return { ...prev, [tableKey]: { key: columnKey, direction: "asc" } };
    });
  };

  // Chunking function
  const chunkArray = (array, chunkCount) => {
    const chunkSize = Math.ceil(array.length / chunkCount);
    const result = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }
    return result;
  };

  const getChartCount = (length) => {
    if (length < 9) return 1;
    if (length >= 9 && length <= 18) return 2;
    if (length <= 45) return 3;
    return Math.ceil(length / 20);
  };

  const chartCount = getChartCount(places.length);
  const chunkedPlaces = chunkArray(places, chartCount);

  return (
    <div>
      {/* Toggle switch */}
      <div className="card hptCard mt-4 border-0">
        <div className="card-header brBtomlr">
          <div className="headnumText justify-content-between">
            <div className="cardTitle">
              <h5>
                Early Infant Diagnosis {getChartLabel(filters, selectedState)}
              </h5>
            </div>

            <div class="cardDropDown gap-3">
              <div style={{ marginRight: "10px", fontWeight: 500 }}>
                {showTable ? "Chart View" : "Table View"}
              </div>
              <Form>
                <Form.Check
                  type="switch"
                  id="chart-table-toggle"
                  onChange={() => setShowTable((prev) => !prev)}
                  checked={showTable}
                />
              </Form>
            </div>
          </div>
        </div>
      </div>

      {showTable ? (
        // Table view remains the same
        <div>
          {descriptions.map((Indicator) => {
            const tableKey = Indicator;
            const sortedPlaces = [...places]; // Can implement sorting as needed
            return (
              <div className="card hptCard mt-4 border-0" key={Indicator}>
                <div className="card-body">
                  <h6 style={{ fontWeight: "bold" }}>{Indicator}</h6>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th className="fw-bold">Place</th>
                        {services.map((service) => (
                          <th
                            key={service}
                            className="fw-bold text-center cursor-pointer"
                            onClick={() => handleSort(tableKey, service)}
                          >
                            {formatServiceName(service)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlaces.map((place) => (
                        <tr key={place}>
                          <td style={{ fontWeight: "bold" }}>{place}</td>
                          {services.map((service) => {
                            const entry = chartDataByService[service]?.find(
                              (row) => row.Place === place,
                            );
                            const value = entry ? entry[Indicator] : "-";

                            return (
                              <td key={service} className="text-center">
                                {service === "Positivity_Rate" && value !== "-"
                                  ? `${value}%`
                                  : value}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Chart view: multiple charts if many places
        descriptions.map((Indicator) => {
          const charts = chunkedPlaces.map((chunk, idx) => {
            const chartData = chunk.map((place) => {
              const rowData = { Place: place };
              services1.forEach((service) => {
                const entry = chartDataByService[service]?.find(
                  (row) => row.Place === place,
                );
                rowData[service] = entry ? entry[Indicator] : 0;
              });
              return rowData;
            });

            return (
              <div
                className="card hptCard mt-4 border-0"
                key={`${Indicator}-${idx}`}
              >
                <div className="card-body">
                  <h6 style={{ fontWeight: "bold" }}>{Indicator}</h6>
                </div>
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 130 }}
                  >
                    <XAxis
                      dataKey="Place"
                      angle={-90}
                      textAnchor="end"
                      interval={0}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      content={<CustomLegend order={serviceOrder} />}
                    />
                    {services1.map((service, i) => (
                      <Bar
                        key={service}
                        dataKey={service}
                        fill={["#00a084", "#ffc40c", "#dd1e47", "#608ce6ff"][i]}
                      >
                        <LabelList
                          dataKey={service}
                          position="top"
                          content={({ x, y, width, value }) => {
                            if (value == null) return null;
                            return (
                              <text
                                x={x + width / 2} // center horizontally
                                y={y - 5} // position slightly above bar
                                fontSize={12}
                                fontWeight="bold"
                                textAnchor="start" // 👈 could also try "middle" for alignment
                                transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                              >
                                {value}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          });

          return charts;
        })
      )}
    </div>
  );
};

export default EIDCharts;
